/*
 * Adds the match-mode choice to the pathname exclusion draft, so users type plain text and the
 * chosen mode supplies the glob wildcards. The mode only shapes the next entry; every stored
 * entry is still a glob, so seeding, display, and the request boundary are unchanged.
 */
import { useState } from 'react';
import { type ExclusionListDraft, useExclusionListDraft } from 'src/hooks/useExclusionListDraft';
import { type ExclusionEntry } from 'src/utils/exclusionEntries';
import { validatePathnameInput } from 'src/utils/exclusionValidation';
import {
  DEFAULT_PATHNAME_MATCH_MODE,
  type PathnameMatchMode,
  toPathnameGlob,
} from 'src/utils/pathnameMatchMode';

export interface PathnameListDraft extends ExclusionListDraft {
  readonly mode: PathnameMatchMode;
  readonly setMode: (mode: PathnameMatchMode) => void;
}

export const usePathnameListDraft = (initialEntries: readonly ExclusionEntry[] = []): PathnameListDraft => {
  const [mode, setModeValue] = useState<PathnameMatchMode>(DEFAULT_PATHNAME_MATCH_MODE);
  const draft = useExclusionListDraft(
    (text, existing) => validatePathnameInput(mode, text, existing),
    initialEntries,
    (text) => toPathnameGlob(mode, text),
  );

  return {
    ...draft,
    mode,
    setMode: (next) => {
      setModeValue(next);
      // The mode changes what the typed text means, so an error about it no longer applies.
      draft.setInput(draft.input);
    },
    reset: () => {
      draft.reset();
      setModeValue(DEFAULT_PATHNAME_MATCH_MODE);
    },
  };
};
